/**
 * Unit Costs — per-service cost economics (AI-8128 phase 1).
 *
 * The fleet table answers "what does one call of this service cost?". It is
 * sortable by unit price on purpose: absolute monthly cost hides the
 * services that are expensive *per call* but low-volume today, and those are
 * the ones that become a problem when traffic grows.
 *
 * Two presentation rules carry meaning rather than style:
 *   - a missing unit price renders as "no usage data", never as $0.00. A
 *     service with cost but no invocation count is a coverage gap, and
 *     showing zero would sort it to the top as the cheapest thing we run.
 *   - `data_through` is surfaced whenever it lags, because a stalled sync
 *     otherwise looks exactly like costs falling.
 */
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowDown, ArrowUp, Search } from 'lucide-react';
import { useUnitCostCoverage, useUnitCosts } from '@/hooks/useUnitCosts';
import UnitCostDetailModal from '@/components/unit-costs/UnitCostDetailModal';
import type { ServiceUnitCostRow, UnitCostPeriod } from '@/api/unitCosts';

const PERIODS: UnitCostPeriod[] = ['1d', '7d', '14d', '30d'];

type SortKey = 'cost' | 'unit' | 'invocations' | 'service';

function usd(value: string | null, digits = 2): string {
  if (value === null) return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  if (n === 0) return '$0.00';
  if (n < 0.01 && digits <= 2) return `$${n.toFixed(6)}`;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
}

/** Reserved capacity as a share of spend, or an em dash when there is none. */
function reservedShare(reserved?: string | null, total?: string | null): string {
  const r = Number(reserved ?? NaN);
  const t = Number(total ?? NaN);
  if (!Number.isFinite(r) || !Number.isFinite(t) || t <= 0) return '—';
  return `${((r / t) * 100).toFixed(0)}%`;
}

function count(n: number): string {
  return n.toLocaleString();
}

/**
 * Cost of 1000 calls.
 *
 * The scale slides because the fleet spans six orders of magnitude: a service
 * paying for idle capacity reads $475.90 per thousand, a busy one $0.000205. A
 * fixed 2-decimal currency format renders the second as free, and a fixed
 * 6-decimal one makes the first unreadable, so the precision follows the
 * magnitude instead.
 */
function per1000(value: string | null): string {
  if (value === null) return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  if (n >= 1) return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (n >= 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(6)}`;
}

/** Days between `iso` and today, or null when absent. */
function daysBehind(iso: string | null): number | null {
  if (!iso) return null;
  const ms = Date.now() - new Date(`${iso}T00:00:00Z`).getTime();
  return Math.floor(ms / 86_400_000);
}

export default function UnitCosts() {
  const [period, setPeriod] = useState<UnitCostPeriod>('30d');
  const [search, setSearch] = useState('');
  const [envFilter, setEnvFilter] = useState<string>('');
  const [sortKey, setSortKey] = useState<SortKey>('cost');
  const [sortDesc, setSortDesc] = useState(true);
  // Which service is expanded lives in the URL, not in state: "why does this
  // one cost that much" is the thing people paste into Slack, and a modal
  // held in component state cannot be linked to.
  const [searchParams, setSearchParams] = useSearchParams();
  const openService = searchParams.get('service');

  const params = useMemo(
    () => ({ period, ...(envFilter ? { env: envFilter } : {}) }),
    [period, envFilter],
  );
  const { data, isLoading, error } = useUnitCosts(params);
  // Same params as the table: a coverage figure scoped differently from the
  // rows it sits above just reads as two numbers disagreeing.
  const { data: coverage } = useUnitCostCoverage(params);

  const envs = useMemo(() => {
    const set = new Set<string>();
    data?.rows.forEach((r) => r.env && set.add(r.env));
    return [...set].sort();
  }, [data]);

  const rows = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    const filtered = q
      ? data.rows.filter((r) => (r.service_slug ?? '').toLowerCase().includes(q))
      : data.rows;

    const value = (r: ServiceUnitCostRow): number => {
      switch (sortKey) {
        case 'unit':
          // Rows without a unit price sort last in either direction: they
          // are unknown, not cheap, so they must never head the table.
          return r.cost_per_million_usd === null
            ? Number.NEGATIVE_INFINITY
            : Number(r.cost_per_million_usd);
        case 'invocations':
          return r.invocations;
        case 'service':
          return 0;
        default:
          return Number(r.total_cost_usd);
      }
    };

    const sorted = [...filtered].sort((a, b) => {
      if (sortKey === 'service') {
        return (a.service_slug ?? '').localeCompare(b.service_slug ?? '');
      }
      return value(a) - value(b);
    });
    return sortDesc && sortKey !== 'service' ? sorted.reverse() : sorted;
  }, [data, search, sortKey, sortDesc]);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setSortDesc((d) => !d);
    else {
      setSortKey(key);
      setSortDesc(true);
    }
  };

  const openDetail = useCallback(
    (slug: string | null) => {
      if (!slug) return; // unmapped rows have no service to open
      const next = new URLSearchParams(searchParams);
      next.set('service', slug);
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  // `replace` on close: opening and closing a service should not leave a trail
  // of history entries the back button has to walk through.
  const closeDetail = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('service');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  /**
   * The detail query needs an env, and the right one is the opened row's own —
   * not the table filter, which may be "all". Falls back to the filter so a
   * pasted link still resolves before the table has loaded.
   */
  const openEnv = useMemo(() => {
    if (!openService) return undefined;
    const row = data?.rows.find((r) => r.service_slug === openService);
    return row?.env ?? (envFilter || undefined);
  }, [openService, data, envFilter]);

  const lag = daysBehind(data?.data_through ?? null);

  const Th = ({ label, sortAs, align = 'right' }: { label: string; sortAs?: SortKey; align?: 'left' | 'right' }) => (
    <th
      className={`px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400 ${
        align === 'right' ? 'text-right' : 'text-left'
      } ${sortAs ? 'cursor-pointer select-none hover:text-slate-600 dark:hover:text-slate-200' : ''}`}
      onClick={sortAs ? () => toggleSort(sortAs) : undefined}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {label}
        {sortAs === sortKey &&
          (sortDesc ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />)}
      </span>
    </th>
  );

  return (
    <div className="space-y-6 animate-fade-in">
      <header>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Service catalogue</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Every service AWS bills us for, what it costs to run, and what 100 calls of it cost.
          Reserved capacity is shown apart from traffic cost because the two lead to opposite
          actions: one is a configuration change, the other is a code or volume change.
        </p>
      </header>

      {/* Summary */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-2xl border border-slate-200/60 bg-white p-4 shadow-card dark:border-slate-800/80 dark:bg-slate-900">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Total cost</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
            {usd(data?.total_cost_usd ?? null)}
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200/60 bg-white p-4 shadow-card dark:border-slate-800/80 dark:bg-slate-900">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Invocations</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
            {count(data?.total_invocations ?? 0)}
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200/60 bg-white p-4 shadow-card dark:border-slate-800/80 dark:bg-slate-900">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            Blended / 1000 calls
          </p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
            {usd(data?.blended_cost_per_1000_usd ?? null)}
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200/60 bg-white p-4 shadow-card dark:border-slate-800/80 dark:bg-slate-900">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            Reserved capacity
          </p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
            {usd(data?.total_reserved_cost_usd ?? null)}
          </p>
          {/* Stated as a share because the absolute number means little until
              you know it is most of the bill. */}
          <p className="mt-1 text-[11px] text-slate-400">
            {reservedShare(data?.total_reserved_cost_usd, data?.total_cost_usd)} of spend, billed
            whether or not anything calls it
          </p>
        </div>
      </div>

      {/* Coverage — how much of the bill this view can explain at all. */}
      {coverage && Number(coverage.total_cost_usd) > 0 && (
        <div className="rounded-2xl border border-slate-200/60 bg-white p-4 text-sm shadow-card dark:border-slate-800/80 dark:bg-slate-900">
          <p className="text-slate-600 dark:text-slate-300">
            <span className="font-semibold">{coverage.attributable_pct}%</span> of{' '}
            {usd(coverage.total_cost_usd)} is attributable to a service.{' '}
            {usd(coverage.no_resource_id_cost_usd)} has no resource id in AWS billing, and{' '}
            {usd(coverage.unmapped_cost_usd)} is on resources whose names did not map to a service.
          </p>
        </div>
      )}

      {/* Label the days actually held, not the days requested: a 30d window over
          a ledger that started last week is a 7d number wearing a 30d label. */}
      {data?.window && data.data_through && (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Showing {data.data_from ?? data.window.start} → {data.data_through}
          {data.data_from && data.data_from > data.window.start
            ? ` — the ${period} window starts ${data.window.start}, but the ledger only holds data from ${data.data_from}.`
            : '.'}{' '}
          AWS only serves resource-level cost for 14 days, so longer windows fill in as the daily
          sync accumulates history.
        </p>
      )}

      {lag !== null && lag > 1 && (
        <div className="flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
          <span>
            Ledger data ends {data?.data_through} ({lag} days ago). The daily sync may be behind —
            resource-level cost ages out of AWS Cost Explorer after 14 days and cannot be recovered
            afterwards.
          </span>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-200/60 bg-white p-0.5 dark:border-slate-700 dark:bg-slate-800">
            {PERIODS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPeriod(p)}
                className={`rounded-md px-3 py-1 text-sm transition ${
                  period === p
                    ? 'bg-brand-500 text-white'
                    : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
          {envs.length > 0 && (
            <select
              value={envFilter}
              onChange={(e) => setEnvFilter(e.target.value)}
              className="rounded-lg border border-slate-200/60 bg-white py-1.5 pl-3 pr-8 text-sm text-slate-700 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            >
              <option value="">All environments</option>
              {envs.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            placeholder="Filter services…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-64 rounded-lg border border-slate-200/60 bg-white py-1.5 pl-9 pr-3 text-sm text-slate-700 shadow-sm placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:placeholder:text-slate-500"
          />
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-200">
          Failed to load unit costs: {(error as Error).message}
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl border border-slate-200/60 bg-white shadow-card dark:border-slate-800/80 dark:bg-slate-900">
        <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
          <thead className="bg-surface-50 dark:bg-slate-800/50">
            <tr>
              <Th label="Service" sortAs="service" align="left" />
              <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Env
              </th>
              <Th label="Cost" sortAs="cost" />
              <th
                className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400"
                title="Provisioned concurrency — capacity held warm, billed on the clock"
              >
                Reserved
              </th>
              <Th label="Invocations" sortAs="invocations" />
              <Th label="$ / 1000 calls" sortAs="unit" />
              <th
                className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400"
                title="Traffic cost only — what 1000 more calls would actually add"
              >
                $ / 1000 (traffic)
              </th>
              <th className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Resources
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white dark:divide-slate-800 dark:bg-slate-900">
            {isLoading && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                  Loading…
                </td>
              </tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                  No cost data in this window. The daily sync populates this from AWS Cost Explorer —
                  run it from the admin endpoint to backfill.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr
                key={`${r.account_id}:${r.service_slug ?? 'unmapped'}:${r.env ?? '-'}`}
                className="transition hover:bg-surface-50 dark:hover:bg-slate-800/40"
              >
                <td className="px-4 py-2 text-sm font-medium text-slate-900 dark:text-slate-100">
                  {r.service_slug ? (
                    <button
                      type="button"
                      onClick={() => openDetail(r.service_slug)}
                      className="text-left font-medium text-brand-600 hover:underline dark:text-brand-400"
                      title="Break this cost down by usage type, day and configuration"
                    >
                      {r.service_slug}
                    </button>
                  ) : (
                    <span className="text-slate-400">unmapped</span>
                  )}
                </td>
                <td className="px-4 py-2 text-sm text-slate-500 dark:text-slate-400">{r.env ?? '—'}</td>
                <td className="px-4 py-2 text-right text-sm tabular-nums text-slate-700 dark:text-slate-200">
                  {usd(r.total_cost_usd)}
                </td>
                <td className="px-4 py-2 text-right text-sm tabular-nums">
                  {Number(r.reserved_cost_usd) > 0 ? (
                    <span
                      className="text-amber-600 dark:text-amber-400"
                      title="Capacity held warm — this cost is there at zero traffic"
                    >
                      {usd(r.reserved_cost_usd)}
                    </span>
                  ) : (
                    <span className="text-slate-300 dark:text-slate-600">—</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right text-sm tabular-nums text-slate-700 dark:text-slate-200">
                  {count(r.invocations)}
                </td>
                <td className="px-4 py-2 text-right text-sm font-semibold tabular-nums text-slate-900 dark:text-slate-100">
                  {r.cost_per_1000_usd === null ? (
                    <span
                      className="text-xs font-normal text-amber-600 dark:text-amber-400"
                      title="Cost recorded but no invocation count — not a free service, a gap in the usage data"
                    >
                      no usage data
                    </span>
                  ) : (
                    per1000(r.cost_per_1000_usd)
                  )}
                </td>
                {/* The marginal figure: what 1000 more calls add, with reserved
                    capacity excluded because it does not move with traffic. */}
                <td className="px-4 py-2 text-right text-sm tabular-nums text-slate-500 dark:text-slate-400">
                  {r.usage_cost_per_1000_usd === null ? '—' : per1000(r.usage_cost_per_1000_usd)}
                </td>
                <td className="px-4 py-2 text-right text-sm tabular-nums text-slate-500 dark:text-slate-400">
                  {r.resource_count}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <UnitCostDetailModal
        serviceSlug={openService}
        env={openEnv}
        period={period}
        onClose={closeDetail}
      />
    </div>
  );
}
